/**
 * CID Autocomplete Component
 * Autocomplete for CID-10 codes with search
 *
 * @version 2025.1.0
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import apiClient from '../../../services/apiClient';

interface CidOption {
  code: string;
  description: string;
}

async function buscarCids(termo: string, limite: number): Promise<CidOption[]> {
  const response = await apiClient.get(
    `/cid-codes?search=${encodeURIComponent(termo)}&limit=${limite}`
  );
  const dados: unknown = response.data?.data ?? response.data;
  if (!Array.isArray(dados)) return [];
  return (dados as CidOption[]).map((cid) => ({ code: cid.code, description: cid.description }));
}

interface CidAutocompleteProps {
  value: string;
  onChange: (code: string, description: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  size?: 'small' | 'medium';
}

export const CidAutocomplete: React.FC<CidAutocompleteProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  required = false,
  label = 'Código CID',
  size,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedOption, setSelectedOption] = useState<CidOption | null>(null);

  const buscar = useCallback(() => buscarCids(inputValue, 20), [inputValue]);

  // O CID é catálogo compartilhado e a maioria dos perfis de empresa não o lê: até aqui a
  // busca voltava vazia e calada, que é indistinguível de "não existe esse código".
  const cids = useListaPermitida<CidOption>('cid-code.read', 'Códigos CID', buscar, {
    habilitado: inputValue.length >= 2,
  });

  // O valor que já veio preenchido precisa do rótulo para aparecer escrito no campo.
  useEffect(() => {
    if (!value || selectedOption || !cids.permitido) return;
    let vivo = true;
    buscarCids(value, 1).then((achados) => {
      if (vivo && achados.length > 0) setSelectedOption(achados[0]);
    });
    return () => {
      vivo = false;
    };
  }, [value, selectedOption, cids.permitido]);

  const options =
    selectedOption && !cids.itens.some((opcao) => opcao.code === selectedOption.code)
      ? [selectedOption, ...cids.itens]
      : [...cids.itens];

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: CidOption | null) => {
    setSelectedOption(newValue);
    if (newValue) {
      onChange(newValue.code, newValue.description);
    } else {
      onChange('', '');
    }
  };

  return (
    <Autocomplete
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={cids.carregando}
      disabled={disabled}
      size={size}
      getOptionLabel={(option) => `${option.code} - ${option.description}`}
      isOptionEqualToValue={(option, val) => option.code === val.code}
      filterOptions={(x) => x}
      noOptionsText={
        cids.bloqueio ??
        (inputValue.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum CID encontrado')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={cids.bloqueio ?? helperText ?? 'Digite o código CID (ex: A00, J06)'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {cids.carregando ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CidAutocomplete;
